<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import {
  FAIR_TOOLTIP_HOVER_MEDIA,
  FAIR_TOOLTIP_SHOW_DELAY_MS,
  FAIR_TOOLTIP_TAP_DISMISS_MS,
  type FairTooltipPlacement,
  type FairTooltipResolvedTrigger,
  type FairTooltipTrigger,
} from '@/ui/tokens/fair-tooltip.data'

/**
 * Tooltip — RedisiginProductPage `1:1457`
 * Desktop hover · 300ms show delay · hide immediate (notes `1:1461` · `31:5188`)
 * Compact tap · no delay · 3–4s or tap-away (`31:4443`)
 */
interface FairTooltipProps {
  content?: string
  placement?: FairTooltipPlacement
  /** Override show delay (Figma default 300ms). Tap trigger ignores this. */
  showDelayMs?: number
  /** hover · tap · auto (auto = hover ≥1024 / tap <1024) */
  trigger?: FairTooltipTrigger
  disabled?: boolean
  /** Force visible for Histoire / matrix */
  previewOpen?: boolean
}

const props = withDefaults(defineProps<FairTooltipProps>(), {
  content: 'Tooltip',
  placement: 'top',
  showDelayMs: FAIR_TOOLTIP_SHOW_DELAY_MS,
  trigger: 'auto',
  disabled: false,
  previewOpen: false,
})

const rootRef = ref<HTMLElement | null>(null)
const open = ref(false)
const mediaHover = ref(true)
let showTimer: ReturnType<typeof setTimeout> | null = null
let dismissTimer: ReturnType<typeof setTimeout> | null = null
let hoverMedia: MediaQueryList | null = null

const resolvedTrigger = computed<FairTooltipResolvedTrigger>(() => {
  if (props.trigger === 'hover' || props.trigger === 'tap') {
    return props.trigger
  }
  return mediaHover.value ? 'hover' : 'tap'
})

const isTap = computed(() => resolvedTrigger.value === 'tap')

const isVisible = computed(
  () => props.previewOpen || (!props.disabled && open.value),
)

const rootClass = computed(() => ({
  'fair-tooltip': true,
  'fair-tooltip--tap': isTap.value,
}))

const bubbleClass = computed(() => ({
  'fair-tooltip__bubble': true,
  [`fair-tooltip__bubble--${props.placement}`]: true,
}))

function clearShowTimer() {
  if (showTimer != null) {
    clearTimeout(showTimer)
    showTimer = null
  }
}

function clearDismissTimer() {
  if (dismissTimer != null) {
    clearTimeout(dismissTimer)
    dismissTimer = null
  }
}

function hide() {
  clearShowTimer()
  clearDismissTimer()
  if (!props.previewOpen) {
    open.value = false
  }
}

function showNow() {
  if (props.disabled || props.previewOpen) {
    return
  }
  clearShowTimer()
  open.value = true
  if (isTap.value) {
    clearDismissTimer()
    dismissTimer = setTimeout(() => {
      hide()
    }, FAIR_TOOLTIP_TAP_DISMISS_MS)
  }
}

function scheduleShow() {
  if (props.disabled || props.previewOpen || isTap.value) {
    return
  }
  clearShowTimer()
  showTimer = setTimeout(() => {
    open.value = true
    showTimer = null
  }, props.showDelayMs)
}

function handlePointerEnter(event: PointerEvent) {
  if (event.pointerType === 'touch' || isTap.value) {
    return
  }
  scheduleShow()
}

function handlePointerLeave() {
  if (isTap.value) {
    return
  }
  hide()
}

function handleFocus() {
  if (isTap.value) {
    return
  }
  scheduleShow()
}

function handleBlur() {
  hide()
}

function handleTriggerClick() {
  if (props.disabled || props.previewOpen) {
    return
  }
  if (isTap.value) {
    if (open.value) {
      hide()
      return
    }
    showNow()
    return
  }
  clearShowTimer()
}

function handleDocPointerDown(event: PointerEvent) {
  if (!isTap.value || !open.value || props.previewOpen) {
    return
  }
  const root = rootRef.value
  if (root && event.target instanceof Node && root.contains(event.target)) {
    return
  }
  hide()
}

function handleKeydown(event: KeyboardEvent) {
  if (event.key === 'Escape' && open.value) {
    hide()
  }
}

function syncHoverMedia() {
  mediaHover.value = hoverMedia?.matches ?? true
}

watch(
  () => props.disabled,
  (disabled) => {
    if (disabled) {
      hide()
    }
  },
)

watch(resolvedTrigger, () => {
  hide()
})

onMounted(() => {
  hoverMedia = window.matchMedia(FAIR_TOOLTIP_HOVER_MEDIA)
  syncHoverMedia()
  hoverMedia.addEventListener('change', syncHoverMedia)
  window.addEventListener('pointerdown', handleDocPointerDown, true)
  window.addEventListener('keydown', handleKeydown)
})

onBeforeUnmount(() => {
  clearShowTimer()
  clearDismissTimer()
  hoverMedia?.removeEventListener('change', syncHoverMedia)
  window.removeEventListener('pointerdown', handleDocPointerDown, true)
  window.removeEventListener('keydown', handleKeydown)
})
</script>

<template>
  <span
    ref="rootRef"
    :class="rootClass"
    @pointerenter="handlePointerEnter"
    @pointerleave="handlePointerLeave"
    @click="handleTriggerClick"
  >
    <span
      class="fair-tooltip__trigger"
      @focusin="handleFocus"
      @focusout="handleBlur"
    >
      <slot />
    </span>

    <Transition name="fair-tooltip">
      <span
        v-if="isVisible"
        :class="bubbleClass"
        role="tooltip"
      >
        <p class="fair-tooltip__text">
          <slot name="content">{{ content }}</slot>
        </p>
      </span>
    </Transition>
  </span>
</template>
